import { getPaperFileDownloadUrl } from "@/api/papers";
import { notifyContrast, notifyError, clearToasts } from "@/utils/notification";

/**
 * Display metadata for the three kinds of file a past paper can carry.
 */
export const PAPER_FILE_KINDS = {
    paper: {
        label: "Past Paper",
        shortLabel: "Paper",
        icon: "pi pi-file-pdf",
        severity: "primary",
    },
    marking_scheme: {
        label: "Marking Scheme",
        shortLabel: "Marking Scheme",
        icon: "pi pi-check-square",
        severity: "info",
        restricted: true,
    },
    answer_sheet: {
        label: "Answer Sheet",
        shortLabel: "Answer Sheet",
        icon: "pi pi-pencil",
        severity: "warn",
        restricted: true,
    },
};

export const kindMeta = (kind) => PAPER_FILE_KINDS[kind] || { label: kind, icon: "pi pi-file", severity: "secondary" };

// Display only — a student never receives these files in the first place.
export const isRestrictedKind = (kind) => !!kindMeta(kind).restricted;

/**
 * The language mediums a paper can be tagged with, for the admin upload form.
 */
export const PAPER_MEDIUM_OPTIONS = [
    { label: "Sinhala", value: "sinhala" },
    { label: "English", value: "english" },
    { label: "Tamil", value: "tamil" },
];

/**
 * Foreign-syllabus grades are English-medium by definition — the backend rejects a language
 * medium on them, so the field is hidden rather than left to fail on submit.
 */
export const isLocalSyllabusGrade = (gradeName) => {
    const g = (gradeName || "").toLowerCase();
    return !g.includes("london") && !g.includes("edexcel") && !g.includes("cambridge");
};

/**
 * Files of one kind, in upload order. Papers can carry several answer sheets, so callers render a
 * list rather than a single slot.
 */
export const filesOfKind = (paper, kind) => (paper?.files || []).filter((file) => file.kind === kind);

/**
 * A file's display name — the admin-set label if there is one, otherwise the kind, with an index
 * appended when there are several of the same kind so two answer sheets are tellable apart.
 */
export const fileDisplayName = (file, index, total) => {
    if (file.label) return file.label;
    const base = kindMeta(file.kind).shortLabel;
    return total > 1 ? `${base} ${index + 1}` : base;
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const DOWNLOAD_STAGGER_MS = 300;

/**
 * Starts a download from the signed URL the backend returns.
 */
const startDownload = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
};

/**
 * Resolves one file's signed URL and starts it.
 */
async function saveFile(paper, file) {
    const { url } = await getPaperFileDownloadUrl(paper._id, file.fileId);
    startDownload(url);
}

/**
 * Downloads one file. Resolves the signed URL through the backend, which re-checks the caller's
 * role — this is the only route that will sign a past paper file.
 */
export async function downloadPaperFile(paper, file) {
    const name = file.label || file.fileName || paper.name;

    try {
        notifyContrast("Downloading...", name);
        await saveFile(paper, file);
    } catch (error) {
        clearToasts();
        const status = error?.response?.status;
        if (status === 403) {
            notifyError("Not available", error?.response?.data?.message || "This file is available to tutors only.");
        } else {
            notifyError("Error!", "Unable to complete your request. Please try again later!");
        }
    }
}

/**
 * Downloads a set of files from one paper.
 */
async function downloadFiles(paper, files) {
    if (files.length === 0) return;
    if (files.length === 1) return downloadPaperFile(paper, files[0]);

    notifyContrast("Downloading...", `${files.length} files from ${paper.name}`);

    const starts = [];
    for (const [index, file] of files.entries()) {
        if (index > 0) await delay(DOWNLOAD_STAGGER_MS);
        starts.push(saveFile(paper, file).then(() => true, () => false));
    }

    // Only a file that never started is worth interrupting for.
    if ((await Promise.all(starts)).some((started) => !started)) {
        clearToasts();
        notifyError("Error!", "Some files could not be downloaded. Please try again later!");
    }
}

/**
 * Every file of one kind — the whole row at once, so a paper with five answer sheets needs one
 * click rather than five.
 */
export function downloadPaperFilesOfKind(paper, kind) {
    return downloadFiles(paper, filesOfKind(paper, kind));
}

/**
 * Human-readable byte size for the file lists.
 */
export const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
